import React, { ReactNode, useEffect } from "react";

export interface PopUpViewProps {
  onClose: () => void;
  children: ReactNode;
  className?: string;
  zIndex?: number;
}

const PopUpView: React.FC<PopUpViewProps> = ({
  onClose,
  children,
  className = "",
  zIndex = 1000,
}) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center"
      style={{ zIndex }}
    >
      <div
        className={`
          fixed bottom-0 left-0 right-0
          md:relative md:bottom-auto md:left-auto md:right-auto
          bg-[#121212] text-white
          rounded-t-[40px]
          md:rounded-[20px]
          transform transition-transform duration-300 ease-out
          translate-y-0
          max-h-[80vh] overflow-hidden
          md:max-h-[90vh] md:max-w-[500px] md:w-full md:mx-4
          border-t-2
          md:border-2
          border-white/5
          ${className}
        `}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-black rounded-full p-1"
          aria-label="Close popup"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="overflow-y-auto max-h-full p-6">{children}</div>
      </div>
    </div>
  );
};

PopUpView.defaultProps = {
  className: "",
  zIndex: 1000,
};

export const usePopUpClose = (onClose: () => void) => ({ closePopUp: onClose });

export { PopUpView };
export default PopUpView;
